// =============================================================================
// supabase/functions/_shared/unleashed/customerMap.ts
// -----------------------------------------------------------------------------
// PURE mappers for the Customer sync (Function 2). No Deno, no imports — so the
// push-customers Edge Function AND the vitest suite share one module (mirrors
// productMap.ts / lookupMap.ts).
//
//   • mapUnleashedCustomer(raw)        : one GET /Customers row → the slim shape
//     the planner (planCustomerSync) diffs against `shelters`.
//   • buildCustomerPayload(input, guid): one DFV Service (shelter) → the
//     POST /Customers/{guid} body.
//
// Match key = CustomerCode (= shelters.shelter_code; immutable once the Customer
// exists). Diff/push field set is IDENTICAL on both sides so "differs" is
// meaningful: name / customer type / sales group / taxable. Warehouse is a
// Sales-Order-header concept, NOT a Customer attribute, so it is deliberately not
// part of the customer diff/push.
//
// ⚠️ SANDBOX-VERIFY (blind-build field-name assumptions, isolated here):
//   • Sales-group source field on the customer (SalesOrderGroup?) — if wrong, GET
//     returns null and every shelter shows a spurious salesGroup diff → obvious,
//     one-field fix on the sandbox.
//   • That Taxable / CustomerType come back exactly as sent.
//   • BankName / BankAccount — CONFIRMED from the Unleashed Customers API docs
//     (the Customer object exposes BankName, BankBranch, BankAccount). We map/push
//     BankName + BankAccount (BankBranch not captured in the portal).
//   • StopCredit — the Customer object has a stop-credit flag; the exact JSON field
//     name (StopCredit) is NOT in our reference doc, so confirm it on the sandbox
//     (a wrong name → GET returns null → a spurious stopCredit diff, one-field fix).
// =============================================================================

export interface SlimCustomer {
  guid: string | null;
  customerCode: string; // match key (= shelter_code)
  customerName: string;
  customerType: string | null;
  salesGroup: string | null;
  taxable: boolean | null;
  stopCredit: boolean | null;
  bankName: string | null;
  bankAccount: string | null;
  // Portal-owned free-text / ABN / warehouse — included so the sync diff can flag a
  // change to any of them (otherwise a customer whose only edit is one of these never
  // appears to push). defaultWarehouse is the Unleashed warehouse CODE (e.g. "W1").
  notes: string | null;
  gstVatNumber: string | null; // ABN
  defaultWarehouse: string | null; // DefaultWarehouse.WarehouseCode
  // Normalized Physical / Postal address keys ("line1 | city | region | postcode") so the
  // sync diff can flag an address-only change. Built with normalizeAddressKey so both
  // sides (Unleashed row + portal shelter) normalize identically. Null = no such address.
  physicalAddr: string | null;
  postalAddr: string | null;
  // All Shipping (delivery) addresses as sorted, de-duped normalized keys, so the diff can
  // compare the whole delivery SET against the portal's `delivery_addresses`. Empty = none.
  shippingAddrs: string[];
  obsolete: boolean;
}

// Normalize an address to a comparable key. Locality → the middle slot (Town/City on the
// Unleashed side, `*_suburb_city` on the portal side), matching how we PUSH addresses.
// Includes line2 so a second-address-line change is detected. Returns null when there's
// no street line (nothing meaningful to compare).
export function normalizeAddressKey(
  line1: unknown,
  line2: unknown,
  locality: unknown,
  region: unknown,
  postcode: unknown
): string | null {
  const parts = [line1, line2, locality, region, postcode].map((v) => String(v ?? "").trim());
  if (!parts[0]) return null;
  return parts.join(" | ");
}

// One portal delivery address (a delivery_addresses row) to push as a Shipping
// address entry. Locality (suburb_city) → Town/City, Suburb left blank — the same
// convention as Physical/Postal. Deactivated rows are filtered out by the caller
// (never sent); a row with no street line is skipped by shippingAddress().
export interface ShippingAddressInput {
  address_name: string | null;
  line1: string | null;
  line2: string | null;
  suburb_city: string | null;
  state: string | null;
  postcode: string | null;
  delivery_instruction: string | null;
  is_default: boolean | null;
}

// The subset of a shelter row the payload needs. Kept as a loose interface (not
// the generated ShelterRow) so this module stays import-free / Deno-safe; the
// caller passes the plain fields off `shelters`.
export interface CustomerPushInput {
  shelter_code: string;
  shelter_name: string;
  customer_type: string | null;
  sales_group: string | null;
  taxable: boolean | null;
  stop_credit: boolean | null;
  // Bank details come from the admin-only shelter_bank_details table (db-127); the
  // caller (push-customers EF) loads them and passes them in.
  bank_name: string | null;
  bank_account: string | null;
  // Default warehouse — stored on shelters as the Unleashed CODE (db-58, e.g. "W1"),
  // pushed as DefaultWarehouse:{WarehouseCode}. Portal-owned.
  default_warehouse: string | null;
  // Physical + Postal address (portal-owned). The single locality field
  // (`*_suburb_city`, "Suburb/City") maps to Unleashed's Town/City (John, 3 Aug 2026);
  // Suburb is left blank. Shipping (delivery) addresses are handled via
  // `shipping_addresses` below (they fold into the same Customer Addresses array).
  physical_line1: string | null;
  physical_line2: string | null;
  physical_suburb_city: string | null;
  physical_state: string | null;
  physical_postcode: string | null;
  postal_line1: string | null;
  postal_line2: string | null;
  postal_suburb_city: string | null;
  postal_state: string | null;
  postal_postcode: string | null;
  // Delivery (Shipping) addresses (portal-owned). When provided and non-empty, the
  // portal's set REPLACES Unleashed's Shipping addresses in the pushed Addresses array
  // (a Customer POST rewrites the whole array; there is NO writable separate
  // delivery-address endpoint — CustomerDeliveryAddresses is GET-only). When undefined
  // or empty, the existing Unleashed Shipping addresses are carried untouched
  // (push-else-carry — never wipe not-yet-imported delivery addresses).
  shipping_addresses?: ShippingAddressInput[];
  // ABN — portal-OWNED (John, 3 Aug 2026): imported, shown, edited and PUSHED. Sent only
  // when the portal HOLDS a value; when empty mergeCustomerForUpdate carries Unleashed's
  // GSTVATNumber so a not-yet-imported ABN is never wiped.
  abn: string | null; // → GSTVATNumber (ABN)
  // internal_notes = Unleashed's Notes, imported ONCE then read-only in the portal (John,
  // 3 Aug 2026). NOT pushed — carried live (CARRY_SCALAR_FIELDS) so a push never blanks or
  // overwrites Unleashed's Notes; push-customers mirrors a changed Notes back into this
  // column (twin of Reminder). Kept on the input only so the EF can compare for that
  // mirror-back. Reminder is likewise GET-only (Unleashed → portal, never written back).
  internal_notes: string | null; // GET-only (carried); mirror-back target
  deactivated: boolean | null;
}

const trimStr = (v: unknown): string => String(v ?? "").trim();
const strOrNull = (v: unknown): string | null => {
  const s = trimStr(v);
  return s === "" ? null : s;
};

// One GET /Customers row → the slim shape used to link + diff.
export function mapUnleashedCustomer(raw: Record<string, unknown>): SlimCustomer {
  return {
    guid: typeof raw.Guid === "string" ? raw.Guid : null,
    customerCode: trimStr(raw.CustomerCode),
    customerName: trimStr(raw.CustomerName),
    customerType: strOrNull(raw.CustomerType),
    salesGroup: strOrNull(raw.SalesOrderGroup ?? raw.SalesGroup),
    taxable: typeof raw.Taxable === "boolean" ? raw.Taxable : null,
    stopCredit: typeof raw.StopCredit === "boolean" ? raw.StopCredit : null,
    bankName: strOrNull(raw.BankName),
    bankAccount: strOrNull(raw.BankAccount),
    notes: strOrNull(raw.Notes),
    gstVatNumber: strOrNull(raw.GSTVATNumber),
    defaultWarehouse: strOrNull(
      (raw.DefaultWarehouse as { WarehouseCode?: unknown } | null | undefined)?.WarehouseCode
    ),
    physicalAddr: addrKeyOf(raw, "Physical"),
    postalAddr: addrKeyOf(raw, "Postal"),
    shippingAddrs: shippingKeysOf(raw),
    obsolete: raw.Obsolete === true,
  };
}

// Pull the given address type out of a GET /Customers row and normalize it for diffing.
function addrKeyOf(raw: Record<string, unknown>, type: string): string | null {
  const addrs = Array.isArray(raw.Addresses) ? (raw.Addresses as Record<string, unknown>[]) : [];
  const a = addrs.find((x) => x && x.AddressType === type);
  if (!a) return null;
  return normalizeAddressKey(a.StreetAddress, a.StreetAddress2, a.City, a.Region, a.PostalCode);
}

// Every Shipping (delivery) address on a GET /Customers row, as sorted, de-duped
// normalized keys (street-less entries dropped). Lets the sync diff compare the whole
// delivery SET against the portal's `delivery_addresses` order-independently.
function shippingKeysOf(raw: Record<string, unknown>): string[] {
  const addrs = Array.isArray(raw.Addresses) ? (raw.Addresses as Record<string, unknown>[]) : [];
  const keys = addrs
    .filter((a) => a && a.AddressType === "Shipping")
    .map((a) => normalizeAddressKey(a.StreetAddress, a.StreetAddress2, a.City, a.Region, a.PostalCode))
    .filter((k): k is string => k !== null);
  return Array.from(new Set(keys)).sort();
}

// The fields an Address accepts on POST (per the API doc's POST Address table) plus the
// two the canonical POST example also sends. Guid / LastModifiedOn are GET-only and part
// of what triggers the HTTP 500, so they are NEVER sent.
const ADDR_KEEP = [
  "AddressType",
  "AddressName",
  "StreetAddress",
  "StreetAddress2",
  "Suburb",
  "City",
  "Region",
  "Country",
  "PostalCode",
  "IsDefault",
  "DeliveryInstruction",
];

// Reduce a GET address row to a clean POST-safe object (drops Guid/LastModifiedOn/etc.).
function cleanAddress(a: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of ADDR_KEEP) {
    const v = a[k];
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  }
  return out;
}

// Build ONE portal-owned address (Physical or Postal) from the shelter fields, starting
// from the existing same-type Unleashed address (if any) so its AddressName / Country /
// DeliveryInstruction / IsDefault are preserved. Locality → Town/City (John, 3 Aug 2026).
function portalAddress(
  type: "Physical" | "Postal",
  line1: string | null,
  line2: string | null,
  locality: string | null,
  state: string | null,
  postcode: string | null,
  existing?: Record<string, unknown>
): Record<string, unknown> {
  const base = existing ? cleanAddress(existing) : {};
  const out: Record<string, unknown> = { ...base, AddressType: type };
  if (!out.AddressName) out.AddressName = type;
  out.StreetAddress = line1 ?? "";
  if (line2) out.StreetAddress2 = line2;
  else delete out.StreetAddress2;
  if (locality) out.City = locality;
  else delete out.City;
  // Suburb intentionally left blank (locality goes to Town/City).
  delete out.Suburb;
  if (state) out.Region = state;
  else delete out.Region;
  if (postcode) out.PostalCode = postcode;
  else delete out.PostalCode;
  if (!out.Country) out.Country = "Australia";
  return out;
}

// One portal delivery address → a POST-safe Shipping entry. Locality → Town/City,
// Suburb blank; Country defaults to Australia. Returns null for a row with no street
// line (nothing to send).
function shippingAddress(s: ShippingAddressInput): Record<string, unknown> | null {
  const line1 = trimStr(s.line1);
  if (!line1) return null;
  const out: Record<string, unknown> = { AddressType: "Shipping" };
  out.AddressName = trimStr(s.address_name) || "Delivery";
  out.StreetAddress = line1;
  if (s.line2) out.StreetAddress2 = s.line2;
  if (s.suburb_city) out.City = s.suburb_city;
  if (s.state) out.Region = s.state;
  if (s.postcode) out.PostalCode = s.postcode;
  if (s.delivery_instruction) out.DeliveryInstruction = s.delivery_instruction;
  if (s.is_default === true) out.IsDefault = true;
  out.Country = "Australia";
  return out;
}

// The full Addresses array to POST. Because a Customer POST REPLACES the whole array
// (and addresses have no POST Guid), we must send every address we want to keep:
//   • Shipping (delivery): when the portal provides a set it OWNS Shipping and REPLACES
//     Unleashed's; when it provides none, carry Unleashed's existing Shipping untouched
//     (push-else-carry, so a not-yet-imported delivery address is never wiped);
//   • Physical / Postal: the portal's values when it holds one, else carry Unleashed's.
function buildCustomerAddresses(
  input: CustomerPushInput,
  existing?: Record<string, unknown>
): Record<string, unknown>[] {
  const existingAddrs = Array.isArray(existing?.Addresses)
    ? (existing.Addresses as Record<string, unknown>[])
    : [];
  const out: Record<string, unknown>[] = [];
  const portalShipping = input.shipping_addresses ?? [];
  if (portalShipping.length > 0) {
    for (const s of portalShipping) {
      const a = shippingAddress(s);
      if (a) out.push(a);
    }
  } else {
    for (const a of existingAddrs) {
      if (a && a.AddressType === "Shipping") out.push(cleanAddress(a));
    }
  }
  const exPhysical = existingAddrs.find((a) => a && a.AddressType === "Physical");
  const exPostal = existingAddrs.find((a) => a && a.AddressType === "Postal");
  if (input.physical_line1) {
    out.push(
      portalAddress(
        "Physical",
        input.physical_line1,
        input.physical_line2,
        input.physical_suburb_city,
        input.physical_state,
        input.physical_postcode,
        exPhysical
      )
    );
  } else if (exPhysical) {
    out.push(cleanAddress(exPhysical));
  }
  if (input.postal_line1) {
    out.push(
      portalAddress(
        "Postal",
        input.postal_line1,
        input.postal_line2,
        input.postal_suburb_city,
        input.postal_state,
        input.postal_postcode,
        exPostal
      )
    );
  } else if (exPostal) {
    out.push(cleanAddress(exPostal));
  }
  return out;
}

// One DFV Service → the POST /Customers/{guid} body. `guid` is the target GUID
// (the existing one for an update; a freshly-minted uuid for a create).
export function buildCustomerPayload(
  input: CustomerPushInput,
  guid: string
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    Guid: guid,
    CustomerCode: input.shelter_code,
    CustomerName: input.shelter_name,
    Currency: { CurrencyCode: "AUD" },
    Obsolete: input.deactivated === true,
  };
  if (input.customer_type) body.CustomerType = input.customer_type;
  // SalesOrderGroup — a plain string customer attribute (same field the SalesOrder
  // uses). Sent so new customers are tagged with their sales group (John, 29 Jul 2026).
  if (input.sales_group) body.SalesOrderGroup = input.sales_group;
  if (input.taxable !== null && input.taxable !== undefined) body.Taxable = input.taxable;
  // StopCredit (bool) + bank details. Field names per the SANDBOX-VERIFY note above.
  if (input.stop_credit !== null && input.stop_credit !== undefined)
    body.StopCredit = input.stop_credit;
  if (input.bank_name) body.BankName = input.bank_name;
  if (input.bank_account) body.BankAccount = input.bank_account;
  // Default warehouse — pushed as {WarehouseCode} when the portal holds one; on an
  // UPDATE, mergeCustomerForUpdate carries Unleashed's current warehouse when empty.
  if (input.default_warehouse)
    body.DefaultWarehouse = { WarehouseCode: input.default_warehouse };
  // Portal-owned ABN — sent only when the portal HOLDS a value; for an UPDATE,
  // mergeCustomerForUpdate carries the blank-prone GSTVATNumber from Unleashed when empty.
  // Notes are NOT pushed: they're imported once then treated like Reminder — carried live
  // from the GET (CARRY_SCALAR_FIELDS) so the portal never edits or overwrites Unleashed's
  // Notes, and push-customers mirrors a changed Notes back into shelters.internal_notes.
  // (Reminder is likewise GET-only; see interface.)
  if (input.abn) body.GSTVATNumber = input.abn;
  // Addresses (create path — no existing to carry): the portal's Physical/Postal + any
  // portal Shipping (delivery) addresses. mergeCustomerForUpdate recomputes this WITH the
  // existing Unleashed Shipping carried in when the portal holds no delivery addresses.
  const addrs = buildCustomerAddresses(input);
  if (addrs.length > 0) body.Addresses = addrs;
  return body;
}

// READ-MODIFY-WRITE for an UPDATE. Two sandbox facts drive this:
//  1. A Customer POST BLANKS any omitted *scalar* field it manages — Notes,
//     DeliveryMethod, DefaultWarehouse, DiscountRate, CreditLimit,
//     PrintPackingSlipInsteadOfInvoice all went null when we sent a subset.
//  2. POSTing the WHOLE GET object back returns HTTP 500 — its read-only nested
//     shapes (Currency object, Addresses/Contacts with Guids + /Date()/ timestamps,
//     CustomerTypeGuid, SellPriceTierReference) are not accepted on POST.
// So we send a CLEAN, POST-safe payload: the proven minimal set (buildCustomerPayload,
// which includes the portal-OWNED GSTVATNumber when the portal holds it; Notes is carried
// live, not pushed) PLUS the Unleashed-only blank-prone scalars carried over from `existing`
// (the raw
// GET /Customers/{guid} row). Fields we DON'T send — Contacts, Website, contact
// phones/email, SellPriceTier, and Reminder (GET-only on the API) — are PRESERVED by
// Unleashed on its side, so they're safe to omit. Addresses ARE sent: the full array is
// rebuilt (Physical/Postal + Shipping) and REPLACES Unleashed's, so buildCustomerAddresses
// carries any Shipping the portal doesn't own (see below).
// UNLEASHED-ONLY scalars the portal doesn't manage but Unleashed BLANKS when omitted.
// "Carry live": read them from the current GET and send them straight back so an update
// never wipes them, and (because they're read fresh each push) never overwrites a newer
// Unleashed-side edit (John, 3 Aug 2026). All PROVEN POST-safe on the sandbox
// (ZephyrACT001 round-trip). Other unobserved scalars (TaxCode/SalesPerson/EORINumber/…)
// are NOT carried yet — a wrong shape (e.g. TaxCode expecting a tax object) could
// re-trigger the HTTP 500; add them only once a real customer shows them blanking AND
// they POST cleanly. We also do NOT carry the primary-contact fields (Email/Phone/…):
// they SURVIVE when omitted and the API requires the FirstName/LastName/Email trio
// together, so a partial send would error. DefaultWarehouse is handled separately.
const CARRY_SCALAR_FIELDS = [
  // Notes: imported once, never edited/pushed by the portal — carried live so a push
  // never blanks or overwrites Unleashed's Notes (twin of Reminder). push-customers also
  // mirrors a changed Notes back into shelters.internal_notes (read-only display).
  "Notes",
  "PaymentTerm",
  "DiscountRate",
  "CreditLimit",
  "HasCreditLimit",
  "PrintPackingSlipInsteadOfInvoice",
  "DeliveryMethod",
];

// PORTAL-OWNED but Unleashed BLANKS on omit: the portal pushes its own value when it
// has one (buildCustomerPayload already put it in the body); when the portal is empty
// we CARRY the existing Unleashed value so it isn't wiped (e.g. a customer whose ABN or
// bank details haven't been imported yet). Notes is NOT here — it's carried live
// (CARRY_SCALAR_FIELDS), never portal-pushed. Reminder is NOT here either — Unleashed
// preserves it on omit. DefaultWarehouse is handled separately (an object, {WarehouseCode}).
const PUSH_ELSE_CARRY_FIELDS = ["GSTVATNumber", "BankName", "BankAccount"];

export function mergeCustomerForUpdate(
  existing: Record<string, unknown>,
  input: CustomerPushInput
): Record<string, unknown> {
  const guid = typeof existing.Guid === "string" ? existing.Guid : "";
  const body = buildCustomerPayload(input, guid);
  for (const k of CARRY_SCALAR_FIELDS) {
    const v = existing[k];
    if (v !== undefined && v !== null) body[k] = v;
  }
  // Portal wins when it set the value; otherwise fall back to Unleashed's current value
  // so a blank-prone field the portal doesn't yet hold is preserved, not blanked.
  for (const k of PUSH_ELSE_CARRY_FIELDS) {
    if (!(k in body)) {
      const v = existing[k];
      if (v !== undefined && v !== null) body[k] = v;
    }
  }
  // DefaultWarehouse: the portal's value already went in via buildCustomerPayload when
  // set. Only when the portal is empty do we carry Unleashed's current warehouse —
  // reduced to CODE only (like Currency:{CurrencyCode}); the GET's full object (with
  // Guid + name) is unnecessary and part of what triggers the 500.
  if (!("DefaultWarehouse" in body)) {
    const wh = existing.DefaultWarehouse as { WarehouseCode?: unknown } | null | undefined;
    if (wh && typeof wh === "object" && typeof wh.WarehouseCode === "string") {
      body.DefaultWarehouse = { WarehouseCode: wh.WarehouseCode };
    }
  }
  // Addresses: REPLACE with the full computed array (carried Shipping + portal-overlaid
  // Physical/Postal), since a Customer POST rewrites the whole collection. Only set it
  // when there's something to send, so a customer with no addresses stays untouched.
  const addrs = buildCustomerAddresses(input, existing);
  if (addrs.length > 0) body.Addresses = addrs;
  return body;
}
